import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ArrowLeft,
  BadgePercent,
  ChevronRight,
  Clock3,
  Heart,
  Loader2,
  MapPin,
  ShoppingBag,
  Sparkles,
  Store,
  Utensils,
} from 'lucide-react';

import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import './FoodRadarNotificationPicks.css';


const FoodRadarNotificationPicks = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const notificationId =
    Number(searchParams.get('id') || 0);

  const landingKey =
    String(searchParams.get('key') || '')
      .trim();

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [data, setData] =
    useState({
      notification: null,
      customer: null,
      vendors: [],
    });


  const trackNotificationConversion = async ({
    eventType,
    vendorId = null,
    orderId = null,
    orderAmount = null,
  }) => {
    if (!notificationId || !landingKey || !eventType) return;

    try {
      await fetch(
        `${window.location.origin}/api/control-center/notifications/public/conversion-event`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            queueId: notificationId,
            landingKey,
            eventType,
            vendorId,
            orderId,
            orderAmount,
          }),
          keepalive: true,
        }
      );
    } catch (trackError) {
      console.warn(
        'Notification conversion tracking warning:',
        eventType,
        trackError
      );
    }
  };


  const buildNotificationOrderLink = (vendor) => {
    if (!vendor?.id) return '';

    const rawLink =
      vendor.menuLink ||
      `/food-radar/order/${vendor.id}`;

    const [pathPart, queryPart = ''] =
      String(rawLink).split('?');

    const params =
      new URLSearchParams(queryPart);

    params.set('fromNotification', '1');
    params.set(
      'notificationId',
      String(notificationId)
    );
    params.set(
      'notificationKey',
      landingKey
    );

    return `${pathPart}?${params.toString()}`;
  };


  const loadNotificationPicks =
    async () => {
      try {
        setLoading(true);
        setError('');

        if (
          !notificationId ||
          !landingKey
        ) {
          throw new Error(
            'Notification link incomplete hai.'
          );
        }

        const response =
          await fetch(
            `${window.location.origin}/api/control-center/notifications/public/notification-pick?id=${encodeURIComponent(
              notificationId
            )}&key=${encodeURIComponent(
              landingKey
            )}`
          );

        const result =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          result?.success === false
        ) {
          throw new Error(
            result?.details ||
              result?.error ||
              'Notification picks load nahi hue.'
          );
        }

        setData({
          notification:
            result.notification || null,
          customer:
            result.customer || null,
          vendors:
            Array.isArray(result.vendors)
              ? result.vendors
              : [],
        });
      } catch (loadError) {
        console.error(
          'Notification picks load error:',
          loadError
        );

        setError(
          loadError.message ||
            'Notification picks load nahi hue.'
        );
      } finally {
        setLoading(false);
      }
    };


  useEffect(() => {
    loadNotificationPicks();
  }, [
    notificationId,
    landingKey,
  ]);


  const vendors = useMemo(
    () =>
      Array.isArray(data.vendors)
        ? data.vendors
        : [],
    [data.vendors]
  );



  const getNotificationImage = (vendor = {}, offer = null) => {
    return (
      offer?.imageUrl ||
      offer?.image_url ||
      vendor?.imageUrl ||
      vendor?.image_url ||
      ''
    );
  };


  const featuredVendor = useMemo(() => {
    if (!vendors.length) return null;

    const featuredVendorId = Number(
      data.notification?.featuredVendorId || 0
    );

    if (featuredVendorId) {
      return (
        vendors.find(
          (vendor) => Number(vendor.id) === featuredVendorId
        ) ||
        vendors[0]
      );
    }

    return (
      vendors.find((vendor) => vendor.notificationFeatured) ||
      vendors[0]
    );
  }, [vendors, data.notification]);


  const moreVendors = useMemo(() => {
    if (!featuredVendor) return vendors;

    return vendors.filter(
      (vendor) =>
        Number(vendor.id) !== Number(featuredVendor.id)
    );
  }, [vendors, featuredVendor]);


  const formatOfferPrice =
    (offer = {}) => {
      const comboPrice =
        Number(
          offer.comboPrice || 0
        );

      const actualPrice =
        Number(
          offer.actualPrice || 0
        );

      if (comboPrice > 0) {
        return `₹${comboPrice}`;
      }

      if (actualPrice > 0) {
        return `₹${actualPrice}`;
      }

      if (
        Number(
          offer.discountValue || 0
        ) > 0
      ) {
        const suffix =
          String(
            offer.discountType || ''
          )
            .toLowerCase()
            .includes('percent')
            ? '% OFF'
            : ' OFF';

        return `${offer.discountValue}${suffix}`;
      }

      return '';
    };


  const openVendor =
    async (vendor) => {
      if (!vendor?.id) return;

      /*
        Notification attribution:
        View Menu click ko conversion event me save karte hain.
        Tracking fail ho to navigation/order flow block nahi hoga.
      */
      await trackNotificationConversion({
        eventType: 'menu_opened',
        vendorId: Number(vendor.id),
      });

      /*
        Customer hamesha FoodRadarOrderForm ke Step 1 se start karega:
        Mode -> Details -> Menu -> Review -> Payment.
        Notification id/key query me preserve hote hain taaki successful
        order ko isi notification se attribute kiya ja sake.
      */
      navigate(
        buildNotificationOrderLink(vendor)
      );
    };


  const openOffer =
    (vendor, offer = null) => {
      if (!vendor?.id) return;

      const offerId =
        Number(
          offer?.id ||
            vendor?.bestOffer?.id ||
            data.notification?.featuredOfferId ||
            0
        ) || 0;

      const fallbackLink =
        `/food-radar/offers?vendorId=${vendor.id}` +
        `${offerId ? `&offerId=${offerId}` : ''}` +
        `&fromNotification=1` +
        `&notificationId=${encodeURIComponent(notificationId)}` +
        `&notificationKey=${encodeURIComponent(landingKey)}`;

      const rawOfferLink =
        vendor.offerLink ||
        fallbackLink;

      const separator =
        String(rawOfferLink).includes('?')
          ? '&'
          : '?';

      const finalOfferLink =
        String(rawOfferLink).includes('notificationId=')
          ? rawOfferLink
          : `${rawOfferLink}${separator}fromNotification=1&notificationId=${encodeURIComponent(
              notificationId
            )}&notificationKey=${encodeURIComponent(
              landingKey
            )}`;

      navigate(finalOfferLink);
    };


  if (loading) {
    return (
      <div className="frnp-page">
        <div className="frnp-state-card">
          <Loader2
            size={28}
            className="frnp-spin"
          />

          <h2>
            Your food picks are loading
          </h2>

          <p>
            Notification me bheje gaye
            vendors ready kiye ja rahe hain.
          </p>
        </div>
      </div>
    );
  }


  if (error) {
    return (
      <div className="frnp-page">
        <div className="frnp-state-card error">
          <ShoppingBag size={30} />

          <h2>
            Picks open nahi hue
          </h2>

          <p>{error}</p>

          <button
            type="button"
            onClick={() =>
              navigate('/food-radar')
            }
          >
            Food Radar Home
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="frnp-page">

      <header className="frnp-topbar">
        <button
          type="button"
          className="frnp-back"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>

        <div>
          <span>
            FOOD RADAR
          </span>

          <h1>
            Notification Picks
          </h1>
        </div>

        <div className="frnp-top-icon">
          <Sparkles size={21} />
        </div>
      </header>


      <main className="frnp-content">

        <section className="frnp-hero">
          <div className="frnp-hero-icon">
            <Utensils size={23} />
          </div>

          <div>
            <span>
              HANDPICKED FOR YOU
            </span>

            <h2>
              {data.notification?.title ||
                'Your food picks are ready'}
            </h2>

            <p>
              Sirf isi notification me
              bheje gaye vendors yahan
              dikh rahe hain.
            </p>
          </div>
        </section>


        {data.customer && (
          <section className="frnp-preference">
            <Heart size={17} />

            <div>
              <strong>
                Your preference
              </strong>

              <p>
                {[
                  data.customer
                    .favoriteFood
                    ? `Food: ${data.customer.favoriteFood}`
                    : '',
                  data.customer
                    .favoriteVendorName
                    ? `Vendor: ${data.customer.favoriteVendorName}`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' • ') ||
                  'Aapke recent order pattern ke hisaab se ranking.'}
              </p>
            </div>
          </section>
        )}


        {featuredVendor && (
          <section className="frnp-featured-card">
            <div className="frnp-featured-label">
              <Sparkles size={15} />
              Notification Pick
            </div>

            <div className="frnp-featured-grid">
              <div className="frnp-featured-image-wrap">
                {getNotificationImage(
                  featuredVendor,
                  featuredVendor.bestOffer
                ) ? (
                  <img
                    className="frnp-featured-image"
                    src={getNotificationImage(
                      featuredVendor,
                      featuredVendor.bestOffer
                    )}
                    alt={
                      featuredVendor.bestOffer?.offerName ||
                      featuredVendor.name
                    }
                    loading="eager"
                  />
                ) : (
                  <div className="frnp-featured-image-fallback">
                    🍽️
                  </div>
                )}
              </div>

              <div className="frnp-featured-copy">
                <span className="frnp-featured-vendor">
                  {featuredVendor.name}
                </span>

                <h2>
                  {featuredVendor.bestOffer?.offerName ||
                    featuredVendor.bestOffer?.offerText ||
                    'Aapke liye special food pick'}
                </h2>

                {featuredVendor.bestOffer?.offerText &&
                  featuredVendor.bestOffer?.offerText !==
                    featuredVendor.bestOffer?.offerName && (
                    <p>
                      {featuredVendor.bestOffer.offerText}
                    </p>
                  )}

                {featuredVendor.bestOffer && (
                  <div className="frnp-featured-price">
                    {formatOfferPrice(
                      featuredVendor.bestOffer
                    )}
                  </div>
                )}

                <div className="frnp-featured-note">
                  Notification me jo pick dekha tha, wahi yahan sabse upar hai.
                </div>

                <div className="frnp-featured-actions">
                  <button
                    type="button"
                    className="frnp-featured-order"
                    onClick={() => openVendor(featuredVendor)}
                  >
                    View Menu
                    <ChevronRight size={18} />
                  </button>

                  {featuredVendor.hasOffer && (
                    <button
                      type="button"
                      className="frnp-featured-offer"
                      onClick={() =>
                        openOffer(
                          featuredVendor,
                          featuredVendor.bestOffer
                        )
                      }
                    >
                      View Offer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}


        <div className="frnp-count-row">
          <div>
            <Store size={17} />

            <strong>
              {vendors.length}
              {' '}
              vendor
              {vendors.length === 1
                ? ''
                : 's'}
            </strong>
          </div>

          <span>
            Notification selection only
          </span>
        </div>


        {moreVendors.length > 0 && (
          <div className="frnp-more-title">
            <div>
              <Sparkles size={16} />
              <strong>More Picks</strong>
            </div>
            <span>{moreVendors.length} more</span>
          </div>
        )}

        <section className="frnp-vendor-list">
          {moreVendors.map((vendor) => {
            const offer =
              vendor.bestOffer || null;

            const offerPrice =
              offer
                ? formatOfferPrice(
                    offer
                  )
                : '';

            return (
              <article
                className="frnp-vendor-card"
                key={vendor.id}
              >
                <div className="frnp-rank">
                  {vendor.rank}
                </div>

                <div className="frnp-card-head">
                  <div className="frnp-vendor-avatar">
                    <Store size={25} />
                  </div>

                  <div className="frnp-vendor-main">
                    <h3>
                      {vendor.name}
                    </h3>

                    {vendor.preferenceLabel && (
                      <span className="frnp-match-badge">
                        <Sparkles size={13} />
                        {vendor.preferenceLabel}
                      </span>
                    )}

                    {!vendor.preferenceLabel && (
                      <span className="frnp-selected-badge">
                        Selected vendor
                      </span>
                    )}
                  </div>

                  {vendor.hasOffer && (
                    <span className="frnp-offer-badge">
                      OFFER
                    </span>
                  )}
                </div>


                {vendor.area && (
                  <div className="frnp-meta-line">
                    <MapPin size={15} />
                    <span>
                      {vendor.area}
                    </span>
                  </div>
                )}


                {vendor.favoriteFood && (
                  <div className="frnp-meta-line">
                    <Utensils size={15} />
                    <span>
                      Preference:
                      {' '}
                      <strong>
                        {vendor.favoriteFood}
                      </strong>
                    </span>
                  </div>
                )}


                {offer ? (
                  <div className="frnp-offer-box">
                    <div className="frnp-offer-icon">
                      <BadgePercent size={19} />
                    </div>

                    <div className="frnp-offer-copy">
                      <span>
                        {offer.preferenceMatch
                          ? 'Offer matching your preference'
                          : 'Available offer'}
                      </span>

                      <strong>
                        {offer.offerName ||
                          offer.offerText ||
                          'Special Offer'}
                      </strong>

                      {offer.offerText &&
                        offer.offerName &&
                        offer.offerText !==
                          offer.offerName && (
                          <small>
                            {offer.offerText}
                          </small>
                        )}
                    </div>

                    {offerPrice && (
                      <div className="frnp-offer-price">
                        {offerPrice}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="frnp-no-offer">
                    <ShoppingBag size={17} />

                    <div>
                      <strong>
                        Menu available
                      </strong>

                      <span>
                        Offer nahi hai, lekin
                        poora menu dekh sakte hain.
                      </span>
                    </div>
                  </div>
                )}


                <div className="frnp-actions">
                  <button
                    type="button"
                    className="frnp-menu-button"
                    onClick={() =>
                      openVendor(vendor)
                    }
                  >
                    View Menu
                    <ChevronRight
                      size={18}
                    />
                  </button>

                  {vendor.hasOffer && (
                    <button
                      type="button"
                      className="frnp-offer-button"
                      onClick={() =>
                        openOffer(
                          vendor,
                          offer
                        )
                      }
                    >
                      View Offer
                      <ChevronRight
                        size={17}
                      />
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </section>


        {!vendors.length && (
          <div className="frnp-empty">
            <Store size={28} />

            <h3>
              Vendor available nahi hai
            </h3>

            <p>
              Is notification ke selected
              vendors abhi load nahi ho pa rahe.
            </p>
          </div>
        )}


        <section className="frnp-footer-note">
          <Clock3 size={18} />

          <p>
            Is page par koi extra vendor
            automatically add nahi hota.
            Notification me jitne vendor
            bheje gaye the, sirf wahi show
            hote hain.
          </p>
        </section>

      </main>
    </div>
  );
};


export default FoodRadarNotificationPicks;
